import { Box, Checkbox, FormControlLabel, ListItemButton, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { onOpenNewEvent } from "rx/appStateSlice";
import { setEventId } from "rx/eventIdSlice";
import { useRFirebaseDb } from "utils/FirebaseDbWrapper";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import { EventType } from "rx/fbListSlices";
import { normalizedLowerCase } from "utils/SmallUtils";
import { CupListElement, useMCupEventIds } from "./CupMgmt";


type GroupedEvents = Record<string, (EventType & { id: string })[]>;

const Row: React.FC<ListChildComponentProps<{ keys: string[], events: GroupedEvents, handleOpen: (id: string) => void }>> = ({ index, style, data }) => {
  const { keys, events, handleOpen } = data;
  const k = keys[index];

  return (
    <div style={style}>
      <ListSubheader sx={{ fontSize: 24, fontWeight: 700 }}>
        {dayjs(events[k][0].starttime).format("YYYY MMMM")}
      </ListSubheader>
      {events[k].map((ev) => (
        <ListItemButton key={ev.id} onClick={() => handleOpen(ev.id)}>
          <ListItemText
            primary={ev.name}
            secondary={dayjs(ev.starttime).format("D MMM")}
          />
        </ListItemButton>
      ))}
    </div>
  );
}

type FBRecords<T> = Record<string, T>

const OpenEventDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const [[filteredEvents, groupkeys], setFilteredEvents] = useState<[GroupedEvents, keys: string[]]>([{}, []]);
  const dispatch = useDispatch();
  const fev = useRFirebaseDb<Record<string, EventType>>("/events");
  const listRef = useRef<VariableSizeList>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fcups = useRFirebaseDb<FBRecords<CupListElement>>("cups/list");
  const [selectedCups, setSelectedCups] = useState<string[]>([]);
  const cupevents = useMCupEventIds(selectedCups);
  const didScrollRef = useRef(false);

  const handleCupToggle = (key: string) => {
    setSelectedCups((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  const currentYear = new Date().getFullYear();
  const cupsThisYear = fcups
    ? Object.entries(fcups)
      .filter(([_, cup]) => cup.year === currentYear)
      .map(([key, cup]) => ({ key, ...cup }))
    : [];
  const allevents = useMemo(() => {
    var sourceevents: GroupedEvents = {};
    if (!fev) return sourceevents;
    for (let ent in fev) {
      const groupid = dayjs(fev[ent].starttime).format("YYYY MM");
      if (sourceevents[groupid] === undefined) sourceevents[groupid] = [];
      sourceevents[groupid].push({ id: ent, ...fev[ent] });
    }
    return sourceevents;
  }, [fev]);
  const events = useMemo(() => {
    if (cupevents.length === 0) return allevents;
    const filtered: GroupedEvents = {};
    Object.entries(allevents).forEach(([group, evs]) => {
      const groupFiltered = evs.filter(ev => cupevents.includes(ev.id));
      if (groupFiltered.length > 0) filtered[group] = groupFiltered;
    });
    return filtered;
  }, [allevents, cupevents])

  useLayoutEffect(() => {
    if (didScrollRef.current) return;

    const currentMonthKey = dayjs().format("YYYY MM");

    if (!listRef.current || groupkeys.length === 0) return;
    const idx = groupkeys.findIndex(key => key === currentMonthKey);
    if (idx >= 0) {
      listRef.current.scrollToItem(idx, "start");
      didScrollRef.current = true;
    }
  }, [groupkeys, events]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [events]);

  useEffect(() => {
    let sc = normalizedLowerCase(searchString);
    var fe: GroupedEvents = {};
    if (events === undefined) {
      setFilteredEvents([{}, []]);
      return;
    }
    Object.entries(events).forEach(([k, v]) => {
      const ge = v.filter(
        (ev) => ev.name && normalizedLowerCase(ev.name).includes(sc)
      );
      if (ge.length > 0) fe[k] = ge.sort((l, r) => (r.starttime || 0) - (l.starttime || 0));
    });
    setFilteredEvents([fe, Object.keys(fe).sort().reverse()]);
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true); // true = force rerender
    }
  }, [events, searchString, setFilteredEvents]);

  const handleSearchStringChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchString(event.target.value);
  };
  const handleOpen = (eventid: string) => {
    dispatch(onOpenNewEvent());
    dispatch(setEventId(eventid));
    window.history.pushState(null, "Title for page", "/" + eventid);
    onClose();
  };
  return (
    <Dialog open={true} maxWidth="sm" fullWidth={true} fullScreen={isMobile} aria-labelledby="open-event-title">
      <DialogTitle id="open-event-title">{t("openevent.title")}</DialogTitle>
      <DialogContent style={{ overflow: "hidden" }}>
        {events === undefined ? (
          <div>
            <CircularProgress />
          </div>
        ) : (
          <Grid container>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                inputRef={searchInputRef}
                aria-label="Search event"
                id="searchev"
                label={t("openevent.search.label")}
                placeholder={t("openevent.search.placeholder")}
                variant="outlined"
                onChange={handleSearchStringChange}
                value={searchString}
              />
              {cupsThisYear.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {cupsThisYear.map((cup) => (
                    <FormControlLabel
                      key={cup.key}
                      control={
                        <Checkbox
                          checked={selectedCups.includes(cup.key)}
                          onChange={() => handleCupToggle(cup.key)}
                        />
                      }
                      label={cup.name || cup.key}
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {groupkeys.length === 0 ? (
                <Box height={window.innerHeight * 0.6}>
                  <Typography sx={{ mt: 2 }}>{t("openevent.noresults", "No results")}</Typography>
                </Box>
              ) :
                <VariableSizeList
                  ref={listRef}
                  height={window.innerHeight * 0.6}
                  width={400}
                  itemSize={(index) => {
                    return filteredEvents[groupkeys[index]].length * 60 + 48;
                  }}
                  itemCount={groupkeys.length}
                  itemData={{
                    keys: groupkeys,
                    events: filteredEvents,
                    handleOpen,
                  }}
                >{Row}</VariableSizeList>}
            </Grid>
          </Grid>
        )}
        <DialogActions>
          <Button
            aria-label="close"
            onClick={onClose}
            color="primary"
            variant="contained"
          >
            {t("button.close")}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default OpenEventDialog;
